/**
 * Consistent Character Test Page - Generate character variations with identity preserved
 * 
 * How it works:
 * 1. Upload your 2D character reference image
 * 2. Describe the pose/action you want (or use presets)
 * 3. AI generates the SAME character in that pose
 * 4. Use generated images as keyframes for ToonCrafter!
 * 
 * Access via: /consistent-char-test (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Loader2, Image as ImageIcon, 
  Upload, Trash2, 
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, ArrowLeft, Eye, EyeOff, Info, 
  User, Zap, Copy, Images, Eraser
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

export default function ConsistentCharTest() {
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [referenceImageUrl, setReferenceImageUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("blurry, low quality, distorted, deformed, ugly, bad anatomy, morphing, 3D render, photorealistic");
  const [numOutputs, setNumOutputs] = useState(1);
  const [guidanceScale, setGuidanceScale] = useState(7.5);
  const [inferenceSteps, setInferenceSteps] = useState(30);
  
  // Advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [transparentBackground, setTransparentBackground] = useState(false);
  
  // Upload ref
  const referenceImageRef = useRef(null);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  
  const pollRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadJobHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/consistent-char-test/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load config:", error);
      toast.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/consistent-char-test/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api.post("/uploads/temp", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Upload error:", error);
      return URL.createObjectURL(file);
    }
  };

  const handleReferenceImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.loading("Uploading character reference...");
    const url = await uploadFile(file);
    setReferenceImageUrl(url);
    toast.dismiss();
    toast.success("Character reference uploaded");
  };

  const handlePresetClick = (presetPrompt) => {
    setPrompt(presetPrompt);
    toast.success("Preset applied!");
  };

  const handleGenerate = async () => {
    if (!referenceImageUrl) {
      toast.error("Please upload a character reference image");
      return;
    }
    if (!prompt.trim()) {
      toast.error("Please describe the pose/action you want");
      return;
    }
    
    setGenerating(true);
    setJobStatus(null);
    
    try {
      const response = await api.post("/consistent-char-test/generate", {
        reference_image_url: referenceImageUrl,
        prompt: prompt.trim(),
        negative_prompt: negativePrompt,
        num_outputs: numOutputs,
        guidance_scale: guidanceScale,
        num_inference_steps: inferenceSteps,
        transparent_background: transparentBackground
      });
      
      if (response.data.success) {
        toast.success("Character generation started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start generation");
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    
    const poll = async () => {
      try {
        const response = await api.get(`/consistent-char-test/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.success("Character generated!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error(response.data.error_message || "Generation failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/consistent-char-test/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadImage = async (imageUrl, jobId, index) => {
    try {
      const response = await fetch(getAssetUrl(imageUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `character_${jobId.slice(0, 8)}_${index}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download image");
    }
  };

  const handleCopyImageUrl = (imageUrl) => {
    navigator.clipboard.writeText(getAssetUrl(imageUrl));
    toast.success("Image URL copied!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const actionPresets = config?.action_presets || {};

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-400" />
                <h1 className="text-lg font-semibold">Consistent Character Lab</h1>
                <Badge variant="outline" className="text-emerald-400 border-emerald-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
              FLUX Kontext
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-emerald-300">FLUX Kontext - Style-Preserving Character Edits!</p>
                    <p className="text-zinc-400 text-xs">
                      Upload your 2D character once, then describe any pose or action. FLUX Kontext is designed to 
                      <strong className="text-emerald-400"> preserve your exact art style</strong> - no photorealistic drift, 
                      no extra fingers, no unwanted "enhancements"!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reference Image Upload */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-emerald-400" />
                  Character Reference
                </CardTitle>
                <CardDescription className="text-xs text-zinc-500">
                  Upload your 2D character (clear, front-facing works best)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="aspect-square max-w-xs mx-auto rounded-lg border border-dashed border-zinc-700 bg-zinc-800/50 flex items-center justify-center overflow-hidden cursor-pointer hover:border-emerald-500/50 transition-colors"
                  onClick={() => referenceImageRef.current?.click()}
                  data-testid="reference-image-upload"
                >
                  {referenceImageUrl ? (
                    <img src={getAssetUrl(referenceImageUrl)} alt="Character Reference" className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-center p-4">
                      <Upload className="w-10 h-10 mx-auto text-zinc-500 mb-2" />
                      <p className="text-sm text-zinc-400">Click to upload</p>
                      <p className="text-xs text-zinc-600">Your 2D character</p>
                    </div>
                  )}
                </div>
                <input 
                  ref={referenceImageRef} 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleReferenceImageUpload} 
                />
                {referenceImageUrl && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full mt-2 text-xs text-zinc-400 hover:text-red-400" 
                    onClick={() => setReferenceImageUrl("")}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Action Presets */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-emerald-400" />
                  Quick Action Presets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(actionPresets).map(([key, value]) => (
                    <Button
                      key={key}
                      variant="outline"
                      size="sm"
                      className="text-xs border-zinc-700 hover:border-emerald-500/50 hover:bg-emerald-500/10 capitalize"
                      onClick={() => handlePresetClick(value)}
                      disabled={generating}
                    >
                      {key.replace(/_/g, ' ')}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custom Prompt */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  Describe the Pose/Action
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Describe the pose, action, or variation you want... e.g., 'Character throwing a powerful punch, dynamic action pose, intense expression'"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[100px] bg-zinc-800/50 border-zinc-700 text-sm"
                  disabled={generating}
                  data-testid="pose-prompt-input"
                />
                <p className="text-[10px] text-zinc-600 mt-2">
                  Tip: Be specific about pose, expression, and action for best results
                </p>
              </CardContent>
            </Card>

            {/* Generation Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-emerald-400" />
                    Generation Settings
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-xs text-zinc-400"
                  >
                    {showAdvanced ? "Hide" : "Show"} Advanced
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Number of Outputs */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400 flex items-center gap-1">
                      <Images className="w-3 h-3" />
                      Number of Variations
                    </Label>
                    <span className="text-xs font-medium text-emerald-400">{numOutputs}</span>
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <Button
                        key={n}
                        variant={numOutputs === n ? "default" : "outline"}
                        size="sm"
                        className={numOutputs === n ? "bg-emerald-600 hover:bg-emerald-700" : "border-zinc-700"}
                        onClick={() => setNumOutputs(n)}
                        disabled={generating}
                      >
                        {n}
                      </Button>
                    ))}
                  </div>
                <p className="text-[10px] text-zinc-600">
                    Generate {numOutputs} variation(s)
                  </p>
                </div>

                {/* Transparent Background Toggle */}
                <div className="flex items-center justify-between py-3 px-3 bg-zinc-800/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Eraser className="w-4 h-4 text-emerald-400" />
                    <div>
                      <Label className="text-xs text-zinc-300">Transparent Background</Label>
                      <p className="text-[10px] text-zinc-500">Remove background for PNG with alpha</p>
                    </div>
                  </div>
                  <Switch
                    checked={transparentBackground}
                    onCheckedChange={setTransparentBackground}
                    disabled={generating}
                  />
                </div>

                {/* Advanced Settings */}
                {showAdvanced && (
                  <div className="space-y-4 pt-2 border-t border-zinc-800">
                    {/* Guidance Scale */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Prompt Adherence</Label>
                        <span className="text-xs font-medium text-emerald-400">{guidanceScale}</span>
                      </div>
                      <Slider
                        value={[guidanceScale]}
                        onValueChange={([v]) => setGuidanceScale(v)}
                        min={1}
                        max={15}
                        step={0.5}
                        disabled={generating}
                        className="py-2"
                      />
                    </div>

                    {/* Inference Steps */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-zinc-400">Quality (Steps)</Label>
                        <span className="text-xs font-medium text-emerald-400">{inferenceSteps}</span>
                      </div>
                      <Slider
                        value={[inferenceSteps]}
                        onValueChange={([v]) => setInferenceSteps(v)}
                        min={20}
                        max={50}
                        step={5}
                        disabled={generating}
                        className="py-2"
                      />
                    </div>

                    {/* Negative Prompt */}
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-400">Negative Prompt</Label>
                      <Textarea
                        placeholder="What to avoid..."
                        value={negativePrompt}
                        onChange={(e) => setNegativePrompt(e.target.value)}
                        className="min-h-[60px] bg-zinc-800/50 border-zinc-700 text-xs"
                        disabled={generating}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !referenceImageUrl || !prompt.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-medium"
              data-testid="generate-consistent-char-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Character...
                </>
              ) : (
                <>
                  <User className="w-5 h-5 mr-2" />
                  Generate Character Pose
                </>
              )}
            </Button>

            {/* Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-emerald-500/50 bg-emerald-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                    </div>
                    <span className="text-xs text-zinc-500">{Math.round(jobStatus.progress_percent)}%</span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.output_images?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-zinc-400 mb-2">Generated {jobStatus.output_images.length} variation(s):</p>
                      <div className="grid grid-cols-2 gap-2">
                        {jobStatus.output_images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={getAssetUrl(img)} 
                              alt={`Variation ${idx + 1}`} 
                              className="w-full rounded-lg bg-black" 
                            />
                            <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-7 w-7 bg-black/70 hover:bg-black/90"
                                onClick={() => handleDownloadImage(img, jobStatus.job_id, idx)}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-7 w-7 bg-black/70 hover:bg-black/90"
                                onClick={() => handleCopyImageUrl(img)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History & Tips */}
          <div className="space-y-4">
            {/* History */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Images className="w-4 h-4 text-emerald-400" />
                    History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} className="h-6 w-6 p-0">
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">No generations yet</div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge variant="outline" className={`text-[10px] ${
                                job.status === "completed" ? "text-green-400 border-green-500/50" :
                                job.status === "failed" ? "text-red-400 border-red-500/50" :
                                "text-emerald-400 border-emerald-500/50"
                              }`}>
                                {job.status}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(job.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-400 mb-2 line-clamp-2">
                              {job.prompt}
                            </p>
                            {job.status === "completed" && job.output_images?.length > 0 && (
                              <div className="grid grid-cols-2 gap-1 mb-2">
                                {job.output_images.slice(0, 4).map((img, idx) => (
                                  <img 
                                    key={idx}
                                    src={getAssetUrl(img)} 
                                    alt={`Output ${idx + 1}`} 
                                    className="w-full rounded bg-black cursor-pointer hover:opacity-80"
                                    onClick={() => handleDownloadImage(img, job.job_id, idx)}
                                  />
                                ))}
                              </div>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="w-full text-[10px] h-7 text-red-400" 
                              onClick={() => handleDeleteJob(job.job_id)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* How It Works */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-emerald-400" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">1</span>
                  <p>Upload your <strong className="text-emerald-400">2D character</strong> reference</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">2</span>
                  <p>Describe the <strong className="text-emerald-400">pose or action</strong> you want</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">3</span>
                  <p>AI generates <strong className="text-emerald-400">same character</strong> in that pose</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-emerald-600 text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 text-[10px]">4</span>
                  <p>Use as <strong className="text-amber-400">ToonCrafter keyframes</strong> for animation!</p>
                </div>
              </CardContent>
            </Card>

            {/* Workflow Tip */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Pro Workflow
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-400">
                <p className="mb-2">For smooth 2D animation:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Generate <strong className="text-emerald-400">pose A</strong> here</li>
                  <li>Generate <strong className="text-emerald-400">pose B</strong> here</li>
                  <li>Use both in <strong className="text-amber-400">ToonCrafter</strong></li>
                  <li>Get smooth animation! 🎬</li>
                </ol>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-zinc-500 space-y-1">
                <p>• Use <strong className="text-emerald-400">clear, front-facing</strong> reference</p>
                <p>• Be <strong className="text-emerald-400">specific</strong> about pose and expression</p>
                <p>• Include <strong className="text-emerald-400">action words</strong> (throwing, jumping)</p>
                <p>• Generate <strong className="text-emerald-400">multiple variations</strong> to pick the best</p>
                <p>• FLUX Kontext <strong className="text-emerald-400">preserves 2D style</strong> better!</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
